import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { QButton, QStack, QTypography, useTheme } from 'quotient/vNext';
import { useCallback, useRef, useState, useMemo } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  DefaultTooltipContent,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts/types/component/Tooltip';

import { BarChartContainer } from './BarChartContainer.styles';
import { ChartLegend } from './ChartLegend';
import { useBarChart } from './hooks/useBarChart';

import { useAutoRotateLabels } from '../../hooks/useAutoRotateLabels';
import { BarChartProps, InBarLabelStyle } from '../../types';
import {
  getChartColors,
  displayLegend,
  formatLabel,
  X_AXIS_LABEL_ANGLE_STRAIGHT,
  X_AXIS_LABEL_ANGLE_ROTATED,
} from '../../utils/chartUtils';

// Constants for in-bar label styling
const DEFAULT_LABEL_FONT_SIZE = 12;
const DEFAULT_LABEL_PADDING = 8;

// Constants for bar styling
const BAR_CORNER_RADIUS = 4;
const LIGHT_BAR_COLOR_FALLBACK = '#CEE9F2'; // primary[200]
const LIGHT_BAR_HOVER_COLOR_FALLBACK = '#76C0D9'; // primary[300]
const TOOLTIP_COLOR_FALLBACK = '#08607A'; // primary[600]

interface InBarLabelProps {
  // Recharts-provided props (optional because Recharts may not provide them)
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: string | number;
  index?: number;
  // Custom props we explicitly pass
  xAxisKey: string;
  yAxisKey: string;
  showValue: boolean;
  formatValue: (value: any, dataPoint?: any, key?: string) => string;
  style: InBarLabelStyle;
  data: any[];
  theme: any;
}

/**
 * Custom label component that renders text inside horizontal bars
 * Shows label and optionally the formatted value with different colors
 * Note: Only supports horizontal orientation.
 */
const InBarLabel = (props: InBarLabelProps) => {
  const { x, y, width, height, value, index, xAxisKey, yAxisKey, showValue, formatValue, style, data, theme } = props;

  // Guard against missing required props
  if (width === undefined || height === undefined || x === undefined || y === undefined || !xAxisKey || !yAxisKey) {
    return null;
  }

  // Convert to numbers
  const xNum = typeof x === 'string' ? parseFloat(x) : x;
  const yNum = typeof y === 'string' ? parseFloat(y) : y;
  const widthNum = typeof width === 'string' ? parseFloat(width) : width;
  const heightNum = typeof height === 'string' ? parseFloat(height) : height;

  if (Number.isNaN(xNum) || Number.isNaN(yNum) || Number.isNaN(widthNum) || Number.isNaN(heightNum)) {
    return null;
  }

  // Get the data point using the index if data array is provided
  const dataPoint = data && index !== undefined && data[index] ? data[index] : {};

  // Get label from x-axis key and value from y-axis key
  const label = dataPoint[xAxisKey] || '';
  const numValue = dataPoint[yAxisKey] !== undefined ? dataPoint[yAxisKey] : value;

  // Color constants with theme fallbacks
  const labelColor = style?.labelColor || theme.palette.neutral.primary[900];
  const valueColor = style?.valueColor || theme.palette.primary[400];

  const mergedStyle = {
    fontSize: style?.fontSize || DEFAULT_LABEL_FONT_SIZE,
    fontWeight: (style?.fontWeight as 'normal' | 'bold') || 'normal',
    padding: style?.padding || DEFAULT_LABEL_PADDING,
  };

  // Position text inside horizontal bar (left-aligned with padding)
  const commonTextProps = {
    x: xNum + mergedStyle.padding,
    y: yNum + heightNum / 2,
    style: {
      fontSize: mergedStyle.fontSize,
      fontWeight: mergedStyle.fontWeight,
    },
    textAnchor: 'start' as const,
    dominantBaseline: 'middle' as const,
  };

  // Render with value or label only
  if (showValue && numValue !== undefined && numValue !== null) {
    const formattedValue = formatValue(numValue, dataPoint, yAxisKey);

    return (
      <text {...commonTextProps}>
        <tspan fill={labelColor}>{label} </tspan>
        <tspan fill={valueColor}>({formattedValue})</tspan>
      </text>
    );
  }

  return (
    <text {...commonTextProps} fill={labelColor}>
      {label}
    </text>
  );
};

export const BarChart = ({
  targetLine,
  onClick,
  displayTitle = false,
  hideXAxis = false,
  hideYAxis = false,
  showFullLabels = false,
  angleLabels = false,
  orientation = 'vertical',
  topNBars,
  aggregatedBarLabel,
  showLabelsInBars = false,
  showValuesInBars = false,
  inBarLabelStyle = {},
  transformNegativeValues = false,
  ...chartProps
}: BarChartProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
  const {
    data,
    formatValue,
    stackGroups,
    xAxisKeys,
    xAxisLabels,
    xAxisLabelSpacing,
    yAxisKeys,
    yAxisLabels,
    yAxisWidth,
  } = useBarChart({
    ...chartProps,
    showFullLabels,
    angleLabels,
    topNBars,
    aggregatedBarLabel,
    transformNegativeValues,
  });

  const stacks: Record<string, number> = {};
  const stackHeights: Record<number, number> = {};
  const currentBar = useRef('');
  const hasStackGroups = !!stackGroups?.length;
  const isSimpleBar = !hasStackGroups && yAxisKeys.length === 1;
  const isHorizontal = orientation === 'horizontal';

  // Auto-rotation logic
  const { containerRef, effectiveAngleLabels } = useAutoRotateLabels({
    data,
    xAxisKeys,
    yAxisWidth,
    hideXAxis,
    hideYAxis,
    isHorizontal,
    angleLabels,
    showFullLabels,
  });

  // Validate: topNBars and in-bar labels only work with simple bars
  if ((topNBars || showLabelsInBars) && !isSimpleBar) {
    // eslint-disable-next-line no-console
    console.warn('topNBars and showLabelsInBars only work with simple (non-stacked, single series) bar charts');
  }

  // Validate: in-bar labels only work with horizontal orientation
  if (showLabelsInBars && !isHorizontal) {
    // eslint-disable-next-line no-console
    console.warn(
      'showLabelsInBars only works with horizontal orientation. Set orientation="horizontal" to use this feature.',
    );
  }

  // Build stacks
  if (hasStackGroups) {
    stackGroups.forEach((group, groupIdx) => {
      group.forEach((key) => {
        stacks[key] = groupIdx;
      });
      stackHeights[groupIdx] = group.length;
    });
  }

  /**
   * Recharts doesn't natively handle per bar per section tooltip hovering in grouped bar charts so we have to
   * calculate it manually.
   */
  const handleTooltipHover = useCallback(
    ({ active, payload }: TooltipProps<ValueType, NameType>) => {
      if (active && payload?.length && currentBar.current) {
        const matchingBar = payload.find((item) => item.dataKey === currentBar.current);
        const xAxisValue = payload[0].payload[xAxisKeys[0]];
        if (matchingBar) {
          // Get the x-axis label for better context
          const xAxisLabelText = xAxisLabels && xAxisLabels.length > 0 ? xAxisLabels[0] : 'Value';
          const formattedLabel = `${xAxisLabelText}: ${formatLabel(xAxisValue)}`;

          return (
            <DefaultTooltipContent
              label={formattedLabel}
              payload={[
                {
                  ...matchingBar,
                  // Pass the full payload data point and dataKey to formatValue
                  value: formatValue(matchingBar.value, matchingBar.payload, String(matchingBar.dataKey)),
                  // Use a darker color for tooltip text (override the light bar color)
                  color: theme.palette.primary[600] || TOOLTIP_COLOR_FALLBACK,
                },
              ]}
            />
          );
        }
      }
      return null;
    },
    [formatValue, xAxisKeys, xAxisLabels, theme],
  );
  // Calculate chart margins based on axis visibility and orientation
  const chartMargins = useMemo(() => {
    // Reduce top margin if title is displayed to avoid double spacing
    const topMargin = displayTitle && chartProps?.title ? 5 : 20;

    if (hideXAxis && hideYAxis) {
      // Both axes hidden: minimal margins
      return { top: displayTitle && chartProps?.title ? 5 : 10, right: 10, bottom: 10, left: 10 };
    }

    if (isHorizontal) {
      // Horizontal bars
      return {
        top: topMargin,
        right: 25,
        bottom: 20,
        left: hideYAxis ? 10 : 200, // No left margin needed if Y-axis is hidden
      };
    }

    // Vertical bars
    return {
      top: topMargin,
      right: 25,
      bottom: xAxisLabelSpacing(xAxisKeys[0]),
      left: 0,
    };
  }, [hideXAxis, hideYAxis, isHorizontal, xAxisKeys, xAxisLabelSpacing, displayTitle, chartProps?.title]);

  return (
    <BarChartContainer data-testid="bar-chart" ref={containerRef}>
      {displayTitle && chartProps?.title && (
        <QTypography sx={{ mb: 0 }} variant="smallHeading">
          {chartProps?.title}
        </QTypography>
      )}
      {displayLegend(yAxisKeys) && (
        <QStack alignItems="flex-start" direction="row" spacing={1} sx={{ mb: 4 }}>
          <QButton
            color="primary"
            data-testid="view-legend-button"
            size="small"
            startIcon={faList}
            variant="text"
            onClick={() => setIsLegendModalOpen(true)}
          >
            View legend
          </QButton>
        </QStack>
      )}
      <ResponsiveContainer key={yAxisKeys.length}>
        <RechartsBarChart
          barCategoryGap="4%"
          barGap={1}
          data={data}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={chartMargins}
          maxBarSize={46}
          onClick={onClick}
        >
          {!hideXAxis && !hideYAxis && (
            <CartesianGrid
              stroke={theme.palette.neutral.primary[100]}
              strokeDasharray="3 3"
              horizontal={isHorizontal}
              vertical={!isHorizontal}
            />
          )}
          {/* For horizontal bars: X-axis shows values, Y-axis shows categories */}
          {/* For vertical bars: X-axis shows categories, Y-axis shows values */}
          {isHorizontal ? (
            <>
              <XAxis
                type="number"
                hide={hideXAxis}
                stroke={theme.palette.neutral.primary[700]}
                tick={{ fill: theme.palette.neutral.primary[700] }}
                tickFormatter={formatValue}
                tickLine={false}
                domain={[0, 'auto']}
              />
              <YAxis
                type="category"
                dataKey={xAxisKeys[0]}
                hide={hideYAxis}
                width={hideYAxis ? 0 : 180}
                stroke={theme.palette.neutral.primary[700]}
                tick={{ fill: theme.palette.neutral.primary[700] }}
                tickFormatter={(value) => formatLabel(value, showFullLabels)}
                tickLine={false}
                padding={{ top: 10, bottom: 10 }}
              />
            </>
          ) : (
            <>
              <XAxis
                angle={effectiveAngleLabels ? X_AXIS_LABEL_ANGLE_ROTATED : X_AXIS_LABEL_ANGLE_STRAIGHT}
                dataKey={xAxisKeys[0]}
                hide={hideXAxis}
                padding={{ right: 10 }}
                stroke={theme.palette.neutral.primary[700]}
                tick={{
                  fill: theme.palette.neutral.primary[700],
                  textAnchor: effectiveAngleLabels ? 'start' : 'middle',
                }}
                tickFormatter={(value) => formatLabel(value, showFullLabels)}
                tickLine={false}
              />
              <YAxis
                minTickGap={0.5}
                hide={hideYAxis}
                padding={{ top: 10 }}
                stroke={theme.palette.neutral.primary[700]}
                tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'end' }}
                tickFormatter={formatValue}
                tickLine={false}
                width={yAxisWidth}
              />
            </>
          )}
          <Tooltip content={handleTooltipHover} cursor={false} />

          {/* Render series bars */}
          {yAxisKeys?.map((dataKey, seriesIndex) => {
            const stackId = stacks[dataKey];
            let colors = chartColors[seriesIndex % chartColors.length];
            let lastBarInStack = true;
            if (hasStackGroups) {
              colors =
                chartColors[(stackGroups[stackId]?.length ?? 0 - (stackHeights[stackId] ?? 0)) % chartColors.length];
              stackHeights[stackId] -= 1;
              lastBarInStack = stackHeights[stackId] <= 0;
            }

            // Use a lighter color for horizontal bars with in-bar labels (anomaly detection style)
            let barFillColor = colors.default;
            let barHoverColor = colors.hover;
            if (isHorizontal && showLabelsInBars && isSimpleBar) {
              barFillColor = theme.palette.primary[200] || LIGHT_BAR_COLOR_FALLBACK;
              barHoverColor = theme.palette.primary[300] || LIGHT_BAR_HOVER_COLOR_FALLBACK;
            }

            // Radius is [topLeft, topRight, bottomRight, bottomLeft]
            // Horizontal: round right side (topRight, bottomRight)
            // Vertical: round top side (topLeft, topRight)
            let barRadius: [number, number, number, number];
            if (lastBarInStack && isHorizontal) {
              barRadius = [0, BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0];
            } else if (lastBarInStack) {
              barRadius = [BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0];
            } else {
              barRadius = [0, 0, 0, 0];
            }

            return (
              <Bar
                activeBar={{ fill: barHoverColor }}
                dataKey={dataKey}
                fill={barFillColor}
                key={dataKey}
                name={yAxisLabels[seriesIndex]}
                radius={barRadius}
                stackId={stackId}
                onMouseOver={() => {
                  currentBar.current = dataKey;
                }}
              >
                {/* In-bar labels (only for horizontal simple bars) */}
                {showLabelsInBars && isSimpleBar && isHorizontal && (
                  <LabelList
                    dataKey={yAxisKeys[0]}
                    content={(props) => (
                      <InBarLabel
                        {...props}
                        data={data}
                        theme={theme}
                        xAxisKey={xAxisKeys[0]}
                        yAxisKey={yAxisKeys[0]}
                        showValue={showValuesInBars}
                        formatValue={formatValue}
                        style={inBarLabelStyle}
                      />
                    )}
                  />
                )}
              </Bar>
            );
          })}

          {/* Render target line */}
          {targetLine !== undefined && targetLine !== null && (
            <>
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.common.white}
                strokeLinecap="round"
                strokeWidth={4}
                y={targetLine}
              />
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.denotive.positive[600]}
                strokeLinecap="round"
                strokeWidth={2}
                y={targetLine}
              />
            </>
          )}
        </RechartsBarChart>
      </ResponsiveContainer>
      <ChartLegend
        chartColors={chartColors}
        labels={yAxisLabels}
        open={isLegendModalOpen}
        onClose={() => setIsLegendModalOpen(false)}
      />
    </BarChartContainer>
  );
};
